import React, { useState, useEffect } from 'react';
import { mobileObjectDetailService } from '../services/mobileObjectDetailService';
import { useApiError } from '../../shared/hooks/useApiError';
import { MessageDialog } from '../../shared/ui';

const MovieDeleteModal = ({ 
  isOpen, 
  onClose, 
  objectId, 
  movies, 
  movieData, 
  onMovieDeleted 
}) => {
  // APIエラーハンドリングフック
  const { 
    showErrorDialog,
    errorMessage,
    handleApiError,
    closeErrorDialog
  } = useApiError(() => setIsLoading(false));
  
  // UI状態
  const [isLoading, setIsLoading] = useState(false); // ローディング状態
  const [selectedMovies, setSelectedMovies] = useState(new Set()); // 選択された動画のインデックス

  // モーダル初期化処理
  useEffect(() => {
    if (isOpen) {
      setSelectedMovies(new Set());
    }
  }, [isOpen]);

  // 動画選択切り替え処理
  const handleMovieToggle = (movieIndex) => {
    setSelectedMovies(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(movieIndex)) {
        newSelected.delete(movieIndex);
      } else {
        newSelected.add(movieIndex);
      }
      return newSelected;
    });
  };

  // 動画削除処理
  const handleDeleteMovies = async () => {
    if (selectedMovies.size === 0) {
      MessageDialog.show('エラー', '削除する動画を選択してください。', null, 'error');
      return;
    }

    await MessageDialog.showConfirm('確認', `選択した${selectedMovies.size}本の動画を削除してもよろしいですか？`, async () => {
    try {
      setIsLoading(true);
      
      // 選択された動画を削除（インデックスの降順で削除してインデックスのずれを防ぐ）
      const sortedIndices = Array.from(selectedMovies).sort((a, b) => b - a);
      const deletedIndices = [];
      const failedIndices = [];
      
      for (const movieIndex of sortedIndices) {
        try {
          // movieDataからobjectMovieIdを取得
          const movieDataItem = movieData[movieIndex];
          if (movieDataItem === null || movieDataItem.objectMovieId === null 
            || movieDataItem.objectMovieId === undefined || movieDataItem.objectMovieId === '' ) {
            failedIndices.push(movieIndex);
            continue;
          }
          
          const result = await mobileObjectDetailService.deleteMovie(objectId, movieDataItem.objectMovieId);
          
          // ビジネスロジックエラーのチェック
          if (result.returnCode !== '200') {
            failedIndices.push(movieIndex);
            continue;
          }
          
          deletedIndices.push(movieIndex);
        } catch (error) {
          failedIndices.push(movieIndex);
        }
      }
      
      // 削除結果の処理
      if (failedIndices.length > 0) {
        MessageDialog.show('エラー', `${failedIndices.length}本の動画の削除に失敗しました。`, null, 'error');
      }

      // 親コンポーネントに削除完了を通知（成功したもののみ）
      if (onMovieDeleted && deletedIndices.length > 0) {
        onMovieDeleted(deletedIndices);
      }

      // 削除が成功した場合のみモーダルを閉じる
      if (deletedIndices.length > 0) {
        onClose();
      }

    } catch (error) {
      handleApiError(new Error('動画の削除に失敗しました。'));
    } finally {
      setIsLoading(false);
    }
    });
  };

  if (!isOpen) return null;

  const styles = {
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 3000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    modalContent: {
      background: 'white',
      padding: '20px',
      borderRadius: '10px',
      maxWidth: '90%',
      maxHeight: '90%',
      overflow: 'auto',
      width: '90%'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '10px',
      borderBottom: '1px solid #ddd'
    },
    title: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#333'
    },
    closeButton: {
      background: '#666',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      padding: '8px 16px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    movieList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '5px',
      marginBottom: '20px',
      justifyContent: 'center',
      alignItems: 'center'
    },
    movieContainer: {
      position: 'relative',
      display: 'inline-block'
    },
    checkbox: {
      position: 'absolute',
      top: '5px',
      left: '5px',
      width: '20px',
      height: '20px',
      cursor: 'pointer',
      zIndex: 1
    },
    movieThumbnail: {
      width: '85px',
      height: '85px',
      objectFit: 'contain',
      borderRadius: '5px',
      backgroundColor: '#2B2222',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '12px'
    },
    selectAllButton: {
      background: '#f0f0f0',
      color: '#333',
      border: '1px solid #ccc',
      borderRadius: '5px',
      padding: '8px 16px',
      cursor: 'pointer',
      fontSize: '14px',
      marginBottom: '15px'
    },
    deleteButton: {
      width: '100%',
      background: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      padding: '12px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold'
    },
    deleteButtonDisabled: {
      width: '100%',
      background: '#ccc',
      color: '#666',
      border: 'none',
      borderRadius: '5px',
      padding: '12px',
      cursor: 'not-allowed',
      fontSize: '16px',
      fontWeight: 'bold'
    },
    loadingText: {
      textAlign: 'center',
      color: '#666',
      fontSize: '14px',
      margin: '20px 0'
    }
  };

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <div style={styles.header}>
          <div style={styles.title}>動画削除</div>
          <button style={styles.closeButton} onClick={onClose}>
            戻る
          </button>
        </div>

        {isLoading ? (
          <div style={styles.loadingText}>動画を削除しています...</div>
        ) : (
          <>
            <div style={styles.movieList}>
              {movies && movies.length > 0 ? (
                movies.map((movie, index) => (
                  <div key={index} style={styles.movieContainer}>
                    <input
                      type="checkbox"
                      checked={selectedMovies.has(index)}
                      onChange={() => handleMovieToggle(index)}
                      style={styles.checkbox}
                    />
                    <div style={styles.movieThumbnail}>
                      動画
                    </div>
                  </div>
                ))
              ) : (
                <div style={styles.loadingText}>動画がありません</div>
              )}
            </div>

            <button 
              style={selectedMovies.size > 0 ? styles.deleteButton : styles.deleteButtonDisabled}
              onClick={handleDeleteMovies}
              disabled={selectedMovies.size === 0 || isLoading}
            >
              動画削除
            </button>
          </>
        )}
      </div>

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="メッセージ"
        message={errorMessage}
        type="error"
      />
    </div>
  );
};

export default MovieDeleteModal;
